import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuth } from '../../utils/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const CATEGORIES = ['Transportation', 'Tea & Snacks', 'Electricity', 'Rent', 'Maintenance', 'Office Supplies', 'Salary', 'Other'];
const CATEGORY_ICONS = {
    'Transportation': '🚚', 'Tea & Snacks': '☕', 'Electricity': '⚡', 'Rent': '🏠',
    'Maintenance': '🔧', 'Office Supplies': '📎', 'Salary': '💰', 'Other': '📋'
};
const CATEGORY_COLORS = {
    'Transportation': '#6366f1', 'Tea & Snacks': '#f59e0b', 'Electricity': '#eab308', 'Rent': '#8b5cf6',
    'Maintenance': '#14b8a6', 'Office Supplies': '#f97316', 'Salary': '#22c55e', 'Other': '#64748b'
};

function ExpensesView({ user, handleApiError, showNotification }) {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ category: 'Transportation', description: '', amount: '', expenseDate: new Date().toISOString().split('T')[0] });
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const now = new Date();
    const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
    const [filterYear, setFilterYear] = useState(now.getFullYear());
    const [filterCategory, setFilterCategory] = useState('All');

    const fetchExpenses = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchAuth(`${API_URL}/api/expenses?month=${filterMonth}&year=${filterYear}`);
            if (res.ok) setExpenses(await res.json());
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }, [filterMonth, filterYear, handleApiError]);

    useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.amount || parseFloat(form.amount) <= 0) {
            showNotification('Please enter a valid amount.', 'error');
            return;
        }
        setSaving(true);
        try {
            const res = await fetchAuth(`${API_URL}/api/expenses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            if (res.ok) {
                showNotification('Expense added successfully!', 'success');
                setForm({ category: 'Transportation', description: '', amount: '', expenseDate: new Date().toISOString().split('T')[0] });
                setShowForm(false);
                fetchExpenses();
            } else {
                const err = await res.json();
                throw new Error(err.message);
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await fetchAuth(`${API_URL}/api/expenses/${id}`, { method: 'DELETE' });
            if (res.ok) {
                showNotification('Expense deleted.', 'success');
                setDeleteConfirm(null);
                fetchExpenses();
            } else {
                const err = await res.json();
                throw new Error(err.message);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const filteredExpenses = filterCategory === 'All' ? expenses : expenses.filter(e => e.category === filterCategory);

    // Category-wise monthly summary
    const categorySummary = {};
    expenses.forEach(e => {
        const cat = e.category;
        categorySummary[cat] = (categorySummary[cat] || 0) + parseFloat(e.amount);
    });
    const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
        { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
        { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];
    const years = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) years.push(y);

    const formatCurrency = (val) => `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div className="page-container">
            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px' }}>
                        <h3 style={{ margin: '0 0 12px', color: '#ef4444' }}>Delete Expense</h3>
                        <p style={{ margin: '0 0 20px', color: '#475569' }}>
                            Are you sure you want to delete this <strong>{deleteConfirm.category}</strong> expense of <strong>{formatCurrency(deleteConfirm.amount)}</strong>?
                        </p>
                        <div className="modal-actions">
                            <button type="button" className="cancel-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                            <button type="button" className="confirm-btn" style={{ background: '#ef4444' }} onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Expense Modal */}
            {showForm && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '480px' }}>
                        <h3 style={{ margin: '0 0 16px' }}>Add New Expense</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Category</label>
                                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="form-input">
                                    {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_ICONS[c]} {c}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Description (optional)</label>
                                <input type="text" className="form-input" placeholder="e.g. Fuel for delivery" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div className="form-group">
                                    <label>Amount (₹)</label>
                                    <input type="number" className="form-input" placeholder="0.00" step="0.01" min="0" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Date</label>
                                    <input type="date" className="form-input" required value={form.expenseDate} onChange={e => setForm({ ...form, expenseDate: e.target.value })} />
                                </div>
                            </div>
                            <div className="modal-actions" style={{ marginTop: '16px' }}>
                                <button type="button" className="cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
                                <button type="submit" className="confirm-btn" disabled={saving}>{saving ? 'Saving...' : 'Add Expense'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Page Header */}
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <h2 className="page-title">Expenses Tracker</h2>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))} className="payment-select" style={{ minWidth: '130px' }}>
                        {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="payment-select" style={{ minWidth: '90px' }}>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button className="btn-primary" onClick={() => setShowForm(true)}>
                        <svg className="button-icon" viewBox="0 0 24 24"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"></path></svg>
                        Add Expense
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                {/* Total Card */}
                <div style={{
                    background: 'linear-gradient(135deg, #6366f115, #6366f108)',
                    border: '1px solid #6366f130',
                    borderRadius: '12px', padding: '18px',
                    display: 'flex', flexDirection: 'column', gap: '4px'
                }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>Total This Month</span>
                    <span style={{ fontSize: '22px', fontWeight: 700, color: '#6366f1' }}>{formatCurrency(totalExpenses)}</span>
                </div>
                {/* Category Cards */}
                {Object.entries(categorySummary).sort((a, b) => b[1] - a[1]).map(([cat, total]) => (
                    <div key={cat} style={{
                        background: `linear-gradient(135deg, ${CATEGORY_COLORS[cat] || '#64748b'}12, ${CATEGORY_COLORS[cat] || '#64748b'}06)`,
                        border: `1px solid ${CATEGORY_COLORS[cat] || '#64748b'}25`,
                        borderRadius: '12px', padding: '18px', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', gap: '4px',
                        outline: filterCategory === cat ? `2px solid ${CATEGORY_COLORS[cat]}` : 'none'
                    }} onClick={() => setFilterCategory(filterCategory === cat ? 'All' : cat)}>
                        <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>
                            {CATEGORY_ICONS[cat]} {cat}
                        </span>
                        <span style={{ fontSize: '18px', fontWeight: 700, color: CATEGORY_COLORS[cat] || '#64748b' }}>{formatCurrency(total)}</span>
                    </div>
                ))}
            </div>

            {/* Expenses Table */}
            <div className="data-display">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0 }}>
                        {filterCategory === 'All' ? 'All Expenses' : `${CATEGORY_ICONS[filterCategory]} ${filterCategory} Expenses`}
                        <span style={{ fontSize: '13px', fontWeight: 400, color: '#94a3b8', marginLeft: '8px' }}>({filteredExpenses.length} records)</span>
                    </h3>
                    {filterCategory !== 'All' && (
                        <button onClick={() => setFilterCategory('All')} style={{ background: 'transparent', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontSize: '12px', color: '#64748b' }}>
                            Show All
                        </button>
                    )}
                </div>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        <div style={{ fontSize: '28px', marginBottom: '8px' }}>💸</div>
                        <p>Loading expenses...</p>
                    </div>
                ) : filteredExpenses.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                        <div style={{ fontSize: '28px', marginBottom: '8px' }}>📭</div>
                        <p>No expenses found for this period.</p>
                    </div>
                ) : (
                    <div className="inventory-table-container">
                        <table className="inventory-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Category</th>
                                    <th>Description</th>
                                    <th>Amount</th>
                                    <th style={{ width: '60px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredExpenses.map(exp => (
                                    <tr key={exp.id}>
                                        <td>{new Date(exp.expenseDate).toLocaleDateString('en-IN')}</td>
                                        <td>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                                                background: `${CATEGORY_COLORS[exp.category] || '#64748b'}15`,
                                                color: CATEGORY_COLORS[exp.category] || '#64748b'
                                            }}>
                                                {CATEGORY_ICONS[exp.category]} {exp.category}
                                            </span>
                                        </td>
                                        <td style={{ color: exp.description ? '#1e293b' : '#94a3b8' }}>
                                            {exp.description || '—'}
                                        </td>
                                        <td style={{ fontWeight: 600 }}>{formatCurrency(exp.amount)}</td>
                                        <td>
                                            <button
                                                onClick={() => setDeleteConfirm(exp)}
                                                style={{ color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }}
                                                title="Delete"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ExpensesView;
